import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { getReports } from "@/lib/reports.functions";

export const Route = createFileRoute("/_authenticated/dashboard/analytics")({
  component: AnalyticsPage,
});

const SITE_URL = "https://www.homesteadconstruction.us";

const ranges = [
  { k: 7, l: "7 days" },
  { k: 30, l: "30 days" },
  { k: 90, l: "90 days" },
];

function pct(n: number) { return `${(n * 100).toFixed(1)}%`; }
function secs(n: number) { return `${Math.round(n)}s`; }

function formatUpdated(ts: number | undefined) {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Math.round((Date.now() - ts) / 1000);
  const rel = diff < 60 ? `${diff}s ago` : diff < 3600 ? `${Math.round(diff / 60)}m ago` : `${Math.round(diff / 3600)}h ago`;
  return `${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${rel}`;
}

function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const fetchReports = useServerFn(getReports);
  const { data, isLoading, error, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["analytics-ga4", days],
    queryFn: () => fetchReports({ data: { days, siteUrl: SITE_URL } }),
    staleTime: 5 * 60_000,
  });

  const ga4 = data?.ga4;
  const ga4Err = data?.errors.ga4;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Analytics</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Live data from Google Analytics 4 · Last updated: {formatUpdated(dataUpdatedAt)}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {ranges.map((r) => (
            <button
              key={r.k}
              onClick={() => setDays(r.k)}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                days === r.k ? "bg-brand text-primary-foreground" : "bg-card border border-border"
              }`}
            >
              {r.l}
            </button>
          ))}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="px-3 py-1.5 rounded-lg text-sm bg-card border border-border inline-flex items-center gap-1.5 disabled:opacity-60"
            title="Refresh now"
          >
            <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>


      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive rounded-lg p-4 text-sm">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> Failed to load analytics</p>
          <p className="text-xs mt-1 break-words">{(error as Error).message}</p>
        </div>
      )}

      {ga4Err && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs">
          <p className="font-semibold text-yellow-700 flex items-center gap-2"><AlertTriangle size={14} /> GA4 issue</p>
          <p className="mt-1 break-words text-foreground">{ga4Err}</p>
        </div>
      )}

      {isLoading && (
        <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Loading analytics…
        </div>
      )}

      {ga4 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Stat label="Sessions" value={ga4.summary.sessions.toLocaleString()} />
            <Stat label="Users" value={ga4.summary.users.toLocaleString()} />
            <Stat label="Pageviews" value={ga4.summary.pageviews.toLocaleString()} />
            <Stat label="Bounce rate" value={pct(ga4.summary.bounceRate)} />
            <Stat label="Avg session" value={secs(ga4.summary.avgDuration)} />
            <Stat label="Live now" value={ga4.realtimeUsers} />
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-display font-semibold mb-3">Sessions over time</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ga4.byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={11} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="sessions" stroke="var(--color-brand)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="pageviews" stroke="var(--color-muted-foreground)" strokeWidth={1} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="font-display font-semibold mb-3">Top pages</h2>
              <table className="w-full text-sm">
                <tbody>
                  {ga4.topPages.map((p) => (
                    <tr key={p.path} className="border-b border-border/50 last:border-0">
                      <td className="py-2 truncate max-w-[260px]">{p.path}</td>
                      <td className="py-2 text-right font-semibold">{p.views.toLocaleString()}</td>
                    </tr>
                  ))}
                  {ga4.topPages.length === 0 && <tr><td className="py-3 text-muted-foreground text-center">No data.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="font-display font-semibold mb-3">Top sources</h2>
              <table className="w-full text-sm">
                <tbody>
                  {ga4.referrers.map((r) => (
                    <tr key={r.source} className="border-b border-border/50 last:border-0">
                      <td className="py-2 truncate max-w-[260px]">{r.source}</td>
                      <td className="py-2 text-right font-semibold">{r.sessions.toLocaleString()}</td>
                    </tr>
                  ))}
                  {ga4.referrers.length === 0 && <tr><td className="py-3 text-muted-foreground text-center">No data.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="font-display font-semibold mb-3">Devices</h2>
              <table className="w-full text-sm">
                <tbody>
                  {ga4.devices.map((d) => (
                    <tr key={d.device} className="border-b border-border/50 last:border-0">
                      <td className="py-2 capitalize">{d.device}</td>
                      <td className="py-2 text-right font-semibold">{d.sessions.toLocaleString()}</td>
                    </tr>
                  ))}
                  {ga4.devices.length === 0 && <tr><td className="py-3 text-muted-foreground text-center">No data.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <h2 className="font-display font-semibold mb-3">Top countries</h2>
              <table className="w-full text-sm">
                <tbody>
                  {ga4.countries.map((c) => (
                    <tr key={c.country} className="border-b border-border/50 last:border-0">
                      <td className="py-2">{c.country}</td>
                      <td className="py-2 text-right font-semibold">{c.sessions.toLocaleString()}</td>
                    </tr>
                  ))}
                  {ga4.countries.length === 0 && <tr><td className="py-3 text-muted-foreground text-center">No data.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-display text-2xl font-bold mt-1 text-ink">{value}</p>
    </div>
  );
}
